import React, { useState, useEffect } from 'react';
import CategoryList from './CategoryList';
import SubCategoryGrid from './SubCategoryGrid';
import AudioListPlayer from './AudioListPlayer';
import LearningResume from './LearningResume';
import { LearningProgress } from '../types/learning';
import { learningService } from '../services/learningService';
import { authFetch, apiUrls } from '../config/api';

interface Sentence {
  Text: string;
  audiourl: string;
  translations?: string[];
  aiexplain?: string;
  soundmark?: string;
}

const EnglishListening: React.FC = () => {
  const [currentView, setCurrentView] = useState<'resume' | 'categories' | 'subcategories' | 'player'>('resume');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [currentSubCategoryIndex, setCurrentSubCategoryIndex] = useState<number>(0);
  const [audioList, setAudioList] = useState<Sentence[]>([]);
  const [totalSubCategories, setTotalSubCategories] = useState<number>(0);
  const [targetRepeats, setTargetRepeats] = useState<number>(1);
  const [currentAudioIndex, setCurrentAudioIndex] = useState<number>(0);
  const [studyStartTime, setStudyStartTime] = useState<number>(Date.now());

  const categories = [
    '2 syllables',
    '3 syllables', 
    '4 syllables',
    '5 syllables',
    '6 syllables',
    '7 syllables',
    '8 syllables',
    '9 syllables',
    '10 syllables',
    '11 syllables',
    '12 syllables',
    '13 syllables',
    '14 syllables',
    '15 syllables',
    '16 syllables',
    '17 syllables',
    '18 syllables',
    '19 syllables',
    '20 syllables',
    '21 syllables',
    '22 syllables',
    '23 syllables',
    '24 syllables',
    '25 syllables',
  ];

  // 保存学习进度 (防抖)
  const saveProgress = React.useCallback(
    debounce(async (category: string, subIndex: number, audioIndex: number, repeats: number, targetRepeats: number, totalAudios: number) => {
      // 计算实际学习时长（分钟）
      const studyTimeMinutes = Math.round((Date.now() - studyStartTime) / (1000 * 60));
      
      await learningService.saveProgress({
        category,
        subCategoryIndex: subIndex,
        audioIndex,
        currentRepeats: repeats,
        targetRepeats,
        lastStudyTime: new Date().toISOString(),
        totalAudiosInCategory: totalAudios,
        studyTimeMinutes: Math.max(studyTimeMinutes, 1) // 至少1分钟
      });
      
      // 重置学习开始时间
      setStudyStartTime(Date.now());
    }, 2000),
    [studyStartTime]
  );

  const handleContinueLearning = async (progress: LearningProgress) => {
    setSelectedCategory(progress.category);
    setCurrentSubCategoryIndex(progress.subCategoryIndex);
    setSelectedSubCategory(`${progress.category} - ${progress.subCategoryIndex + 1}`);
    setTargetRepeats(progress.targetRepeats);
    setCurrentAudioIndex(progress.audioIndex);
    setTotalSubCategories(Math.ceil(progress.totalAudiosInCategory / 20));
    
    // 加载对应的音频数据
    const response = await authFetch(apiUrls.sentences({ 
      category: progress.category, 
      page: progress.subCategoryIndex, 
      limit: 20 
    }));
    const result = await response.json();
    
    if (result.success) {
      setAudioList(result.data.sentences);
      setCurrentView('player');
      setStudyStartTime(Date.now()); // 重置学习开始时间
    }
  };

  const handleNewStart = () => {
    setCurrentView('categories');
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setCurrentView('subcategories');
  };

  const handleSubCategorySelect = (subCategory: string, audioData: Sentence[], subIndex: number, totalSubs: number) => {
    setSelectedSubCategory(subCategory);
    setCurrentSubCategoryIndex(subIndex);
    setTotalSubCategories(totalSubs);
    setAudioList(audioData);
    setCurrentAudioIndex(0);
    setCurrentView('player');
    setStudyStartTime(Date.now()); // 重置学习开始时间
  };

  const handleNextSubCategory = async () => {
    const nextIndex = currentSubCategoryIndex + 1;
    if (nextIndex < totalSubCategories) {
      const response = await authFetch(apiUrls.sentences({ 
        category: selectedCategory, 
        page: nextIndex, 
        limit: 20 
      }));
      const result = await response.json();
      
      if (result.success) {
        const nextSubCategory = `${selectedCategory} - ${nextIndex + 1}`;
        setSelectedSubCategory(nextSubCategory);
        setCurrentSubCategoryIndex(nextIndex);
        setAudioList(result.data.sentences);
        setCurrentAudioIndex(0);
        return true;
      }
    }
    return false;
  };

  const handleAudioIndexChange = (index: number) => {
    setCurrentAudioIndex(index);
    // 保存进度
    if (selectedCategory) {
      const totalAudios = totalSubCategories * 20;
      saveProgress(selectedCategory, currentSubCategoryIndex, index, 0, targetRepeats, totalAudios);
    }
  };

  const handleBack = () => {
    if (currentView === 'player') {
      setCurrentView('subcategories');
    } else if (currentView === 'subcategories') {
      setCurrentView('categories');
    } else if (currentView === 'categories') {
      setCurrentView('resume');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {currentView === 'resume' && (
        <LearningResume
          onContinue={handleContinueLearning}
          onNewStart={handleNewStart}
        />
      )}
      
      {currentView === 'categories' && (
        <CategoryList 
          categories={categories}
          onCategorySelect={handleCategorySelect}
        />
      )}
      
      {currentView === 'subcategories' && (
        <SubCategoryGrid
          category={selectedCategory}
          onSubCategorySelect={handleSubCategorySelect}
          onBack={handleBack}
        />
      )}
      
      {currentView === 'player' && (
        <AudioListPlayer
          audioList={audioList}
          title={selectedSubCategory}
          targetRepeats={targetRepeats}
          initialAudioIndex={currentAudioIndex}
          onTargetRepeatsChange={setTargetRepeats}
          onAudioIndexChange={handleAudioIndexChange}
          onNextSubCategory={handleNextSubCategory}
          hasNextSubCategory={currentSubCategoryIndex < totalSubCategories - 1}
          onBack={handleBack}
        />
      )}
    </div>
  );
};

// 防抖函数
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

export default EnglishListening;
